/** Rated capability of an AI system on a step, ordered from least to most automatable. */
export const CAPABILITY_LEVELS = ['NOT_WRITABLE', 'NEEDS_CONTEXT', 'ROUTINE', 'FULLY_SPECIFIED'] as const;
export type CapabilityLevel = (typeof CAPABILITY_LEVELS)[number];

export type ErrorCostLevel = 'IRREVERSIBLE' | 'COSTLY' | 'NOTICEABLE' | 'MINOR' | 'MACHINE_CHECKED' | 'TRIVIAL';

export type AutomationAssessmentState = 'queued' | 'started' | 'finished' | 'failed';

/** How hard a task's typical error hits one business driver, from no effect to lasting harm. */
export const BUSINESS_IMPACT_LEVELS = ['NONE', 'MINOR', 'NOTICEABLE', 'SIGNIFICANT', 'CRITICAL'] as const;
export type BusinessImpactLevel = (typeof BUSINESS_IMPACT_LEVELS)[number];

/** The five business drivers in the backend's order: revenue or funding, cost, risk and safety, people served, compliance. */
export const BUSINESS_IMPACT_DRIVERS = ['revenue', 'cost', 'risk', 'peopleServed', 'compliance'] as const;
export type BusinessImpactDriver = (typeof BUSINESS_IMPACT_DRIVERS)[number];
export type BusinessImpactDrivers = Readonly<Record<BusinessImpactDriver, BusinessImpactLevel>>;

/**
 * Display constants mirroring the backend's step scoring. They only turn a stored label into a
 * position on a scale for the tiles; every score shown comes from the backend as stored.
 */
export const CAPABILITY_FACTOR: Record<CapabilityLevel, number> = {
    FULLY_SPECIFIED: 1,
    NEEDS_CONTEXT: 0.5,
    NOT_WRITABLE: 0,
    ROUTINE: 1,
};

export const ERROR_COST_RATING: Record<ErrorCostLevel, number> = {
    COSTLY: 3,
    IRREVERSIBLE: 1,
    MACHINE_CHECKED: 10,
    MINOR: 7,
    NOTICEABLE: 5,
    TRIVIAL: 10,
};

export type StepAssessment = {
    /** Capability label resolved to its 0-1 factor. */
    readonly capability: number;
    /** The rated label itself, for the scale the step tile draws. */
    readonly capabilityLevel: CapabilityLevel;
    readonly coordinationShare: number;
    readonly coreInteractionShare: number;
    readonly digitalShare: number;
    readonly effortShare: number;
    /** Error cost label resolved to its 1-10 rating. */
    readonly errorCost: number;
    /** The rated label itself, which the business impact weighs on its own scale. */
    readonly errorCostLevel: ErrorCostLevel;
    readonly mandatedHumanInvolvement: boolean;
    readonly normalizedEffortShare: number;
    readonly rationale: string;
    /** Share of this step's effort that software takes over, 0-1. */
    readonly score: number;
    readonly workflowStepId: string;
};

/**
 * How hard the task's typical error hits five business drivers, rated alongside the automation but independent
 * of it and combined into the value the matrix plots for the task.
 */
export type BusinessImpact = {
    readonly drivers: BusinessImpactDrivers;
    /** The model's reason in one or two sentences; absent when it gave none. */
    readonly rationale: string | undefined;
    /** The task's matrix value, 0-1; absent from backends that do not send it. */
    readonly value: number | undefined;
};

export type AutomationAssessment = {
    /** Undefined on assessments without the rating: older ones, ones by people, or when the model gave none. */
    readonly businessImpact: BusinessImpact | undefined;
    readonly methodVersion: string;
    readonly steps: readonly StepAssessment[];
    readonly summary: string;
    /** Share of the whole task effort that software takes over, 0-1. */
    readonly taskScore: number;
    readonly taskScoreLower: number;
    readonly taskScoreUpper: number;
};
